import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import { useAtom } from "jotai";
import { themeAtom, currentStaffDataAtom } from "../../../-Atoms";
import { db, doc, updateDoc } from "../../../firebaseConfig";
import { debounce } from "lodash";
// Import the full InvoicingTab component for the Invoice Data tab
import InvoicingTab from "./JobTab/InvoicingTab";

// OperatorChecklistItems definitions
const OPERATOR_CHECKLIST_ITEMS = {
  true: [
    "Ensure all selects are processed",
    "Check job card for notes about supply",
    "Finish storyboards",
    "Export storyboard selects",
    "Process quickproofs (leave them in the output folder)",
    "Put storyboard selects & storyboards into Dropbox",
    "Ensure backups complete",
    "Put JPEGs on working jobs",
    "Fill out images processed",
  ],
  false: [
    "Ensure all selects are processed",
    "Check job card for notes about supply",
    "Finish storyboards",
    "Export storyboard selects",
    "Process quickproofs",
    "Put storyboard selects & storyboards into Dropbox",
    "Put quickproofs into Dropbox",
    "Ensure backups complete",
    "Put JPEGs on working jobs",
    "Fill out images processed",
  ],
};

// Debounced Firebase update function (stable identity)
const updateStoryboardChecklist = debounce(
  async (jobData, selectsStatus, checklistItems) => {
    try {
      const allCompleted = checklistItems.every((item) => item.checked);

      const storyboardChecklistData = {
        selectsStatus,
        items: checklistItems,
        completedAt: allCompleted ? new Date() : null,
        lastModified: new Date(),
      };

      const operatorChecklist = {
        ...jobData?.operatorChecklist,
        storyboardChecklistItems: storyboardChecklistData,
      };

      await updateDoc(doc(db, "JobData", jobData.id), {
        operatorChecklist,
      });
    } catch (error) {
      console.error("Error in batched storyboard checklist update:", error);
    }
  },
  1000
);

const StoryboardOperatorChecklist = ({
  selectsStatus,
  jobData,
  clientData,
  onComplete,
  disabled = false,
}) => {
  const [theme] = useAtom(themeAtom);
  const [currentStaffData] = useAtom(currentStaffDataAtom);

  const [checklistItems, setChecklistItems] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  // Keep latest operatorChecklist in a ref so we can pass it to the updater
  // without causing the update effect to depend on it (prevents feedback loops).
  const operatorChecklistRef = useRef(jobData?.operatorChecklist);
  useEffect(() => {
    operatorChecklistRef.current = jobData?.operatorChecklist;
  }, [jobData?.operatorChecklist]);

  // Initialize checklist items
  useEffect(() => {
    if (selectsStatus === null || !OPERATOR_CHECKLIST_ITEMS[selectsStatus]) {
      setChecklistItems([]);
      return;
    }

    const items = OPERATOR_CHECKLIST_ITEMS[selectsStatus];

    // Check if we have existing progress in jobData
    const existingItems =
      jobData?.operatorChecklist?.storyboardChecklistItems?.items || [];
    const existingItemsMap = existingItems.reduce((acc, item) => {
      acc[item.text] = item;
      return acc;
    }, {});

    // Initialize items with existing state or default unchecked
    const initializedItems = items.map((text, index) => ({
      id: `item_${index}`,
      text,
      checked: existingItemsMap[text]?.checked || false,
      completedAt: existingItemsMap[text]?.completedAt || null,
      completedBy: existingItemsMap[text]?.completedBy || null,
    }));

    setChecklistItems(initializedItems);
  }, [selectsStatus, jobData?.operatorChecklist?.storyboardChecklistItems?.items]);

  // Check completion status
  useEffect(() => {
    const checklistCompleted =
      checklistItems.length > 0 && checklistItems.every((item) => item.checked);
    onComplete(checklistCompleted);
  }, [checklistItems, onComplete]);

  // Memoize stable bits
  const memoizedJobId = useMemo(() => jobData.id, [jobData.id]);
  const memoizedChecklistItems = useMemo(() => checklistItems, [checklistItems]);
  const memoizedSelectsStatus = useMemo(() => selectsStatus, [selectsStatus]);

  // Signature of the data we actually care about for deciding whether to write.
  const makeSignature = (items, status) =>
    `${status || ""}|` +
    items.map((i) => `${i.text}:${i.checked ? 1 : 0}:${i.completedBy || ""}`).join("|");

  // Track last-sent signature to prevent redundant writes (and feedback loops
  // from server-side timestamps like lastModified).
  const lastSentSigRef = useRef(null);

  // Trigger debounced Firebase update when the *meaningful* checklist state changes.
  useEffect(() => {
    if (!memoizedJobId || !memoizedSelectsStatus || memoizedChecklistItems.length === 0) {
      return;
    }

    const sig = makeSignature(memoizedChecklistItems, memoizedSelectsStatus);

    // On first population (mount/initial props), don't write just record signature.
    if (lastSentSigRef.current === null) {
      lastSentSigRef.current = sig;
      return;
    }

    // If nothing meaningful changed, do nothing.
    if (sig === lastSentSigRef.current) return;

    // Otherwise update and record the new signature immediately to avoid loops.
    lastSentSigRef.current = sig;

    updateStoryboardChecklist(
      { id: memoizedJobId, operatorChecklist: operatorChecklistRef.current },
      memoizedSelectsStatus,
      memoizedChecklistItems
    );
  }, [memoizedChecklistItems, memoizedJobId, memoizedSelectsStatus]);

  const completedCount = checklistItems.filter((item) => item.checked).length;
  const totalCount = checklistItems.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const isDark = theme === "dark";

  const themeStyles = {
    background: isDark ? "#1e1e1e" : "#ffffff",
    color: isDark ? "#ffffff" : "#000000",
    borderColor: isDark ? "#2c2c2e" : "#d1d1d6",
    paperBg: isDark ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
  };

  if (selectsStatus === null || checklistItems.length === 0) {
    return null;
  }

  const checklistCompleted = checklistItems.every((item) => item.checked);

  return (
    <Box
      sx={{
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: themeStyles.paperBg,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: isDark
          ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
          : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
        padding: 1.5,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          Operator Checklist
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Checklist: {completedCount}/{totalCount}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ flex: 1, height: 8, borderRadius: 4 }}
          />
        </Box>

        {!checklistCompleted && (
          <Typography variant="caption" color="text.secondary">
            Complete checklist to continue
          </Typography>
        )}
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{
          mb: 2,
          "& .MuiTab-root": {
            color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
          },
          "& .Mui-selected": {
            color: isDark ? "#90caf9" : "#1976d2",
          },
          "& .MuiTabs-indicator": {
            backgroundColor: isDark ? "#90caf9" : "#1976d2",
          },
        }}
      >
        <Tab
          label={`Checklist Items ${
            checklistCompleted ? "✓" : `(${completedCount}/${totalCount})`
          }`}
        />
        <Tab label="Invoice Data" />
      </Tabs>

      {activeTab === 0 && (
        <Stack spacing={0.5}>
          {checklistItems.map((item, index) => (
            <ChecklistItem
              key={item.id}
              item={item}
              index={index}
              checklistItems={checklistItems}
              setChecklistItems={setChecklistItems}
              currentStaffData={currentStaffData}
              disabled={disabled}
              isDark={isDark}
              themeStyles={themeStyles}
            />
          ))}
        </Stack>
      )}

      {activeTab === 1 && (
        <Box sx={{ minHeight: 200 }}>
          <InvoicingTab
            jobData={jobData}
            clientData={clientData}
            setHasUnsavedChanges={() => {}}
          />
        </Box>
      )}
    </Box>
  );
};

const ChecklistItem = ({
  item,
  index,
  checklistItems,
  setChecklistItems,
  currentStaffData,
  disabled,
  isDark,
  themeStyles,
}) => {
  const [checked, setChecked] = useState(item.checked);

  // Sync with prop changes (following OperatorChecklistModalV2 pattern)
  useEffect(() => {
    setChecked(item.checked);
  }, [item.checked]);

  const toggleCheck = () => {
    if (disabled) return;

    const now = new Date();
    const newChecked = !checked;

    // Update local state immediately (optimistic update)
    setChecked(newChecked);

    // Prefer immutability: create a new array with the updated item
    setChecklistItems((prev) =>
      prev.map((it) =>
        it.id === item.id
          ? {
              ...it,
              checked: newChecked,
              completedAt: newChecked ? now : null,
              completedBy: newChecked ? currentStaffData?.id || "unknown" : null,
            }
          : it
      )
    );
  };

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={toggleCheck}
          disabled={disabled}
          color="primary"
        />
      }
      label={
        <Typography
          variant="body2"
          sx={{
            textDecoration: checked ? "line-through" : "none",
            opacity: checked ? 0.7 : 1,
            fontWeight: checked ? 400 : 500,
            ml: 1,
          }}
        >
          {item.text}
        </Typography>
      }
      sx={{
        margin: 0,
        padding: "8px 12px",
        userSelect: "none",
        border: `1px solid ${checked ? "#4caf50" : themeStyles.borderColor}`,
        borderRadius: "8px",
        backgroundColor: checked
          ? isDark
            ? "rgba(76, 175, 80, 0.1)"
            : "rgba(76, 175, 80, 0.05)"
          : "transparent",
        transition: "all 0.15s ease",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: checked
            ? isDark
              ? "rgba(76, 175, 80, 0.15)"
              : "rgba(76, 175, 80, 0.1)"
            : isDark
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.02)",
          transform: "translateY(-1px)",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        },
      }}
    />
  );
};

export default StoryboardOperatorChecklist;
